/** @jsxImportSource theme-ui */
import { Text, get, Box, Grid, Alert, Button } from 'theme-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useActor, useMachine } from '@xstate/react';
import { Table } from '@component-controls/components';
import companiesMachine, {
    FILTER_COMPANIES,
    FIX_ERROR_BY_RETRYING,
    LOAD_MORE,
} from '@/containers/companies/machines/companies/companiesMachine';
import { useUiService } from '@/providers/UIProvider';
import { getErrorMessage } from '@/utils/helpers';
import Filters from './Filters';
import SkeletonLoader from '@/components/SkeletonLoader';
import { Company } from './types';
import { columnKeys } from './columnKeys';
import { addActionsToRow, ColumnAction, createColumns, createSelectionColumn } from './columns';
import { TAKE_ITEMS } from '@/env/environment';
import BulkActionBar from './BulkActionBar';
import BulkAddFeatureFlagModal from './BulkAddFeatureFlagModal';

interface TableRow extends Company {
    Actions?: ColumnAction[];
}

const Companies = () => {
    const [current, send] = useMachine(companiesMachine);
    const uiService = useUiService();
    const [uiState] = useActor(uiService);
    const [selectionMap, setSelectionMap] = useState<Map<string, Company>>(new Map());
    const [activeBulkAction, setActiveBulkAction] = useState<string | null>(null);

    const collapsed = uiState.hasTag('collapsed');

    const { ref, inView } = useInView({
        initialInView: true,
        rootMargin: '100px',
        threshold: 0.1,
    });

    const { Results: results, Total: total } = current.context;
    const companiesData = useMemo(() => results.map((row) => addActionsToRow(row)), [results]);

    const isLoading = current.matches('loading') || current.matches('filtering');
    const isError = current.matches('error');

    const selectedIds = useMemo(() => new Set(selectionMap.keys()), [selectionMap]);
    const selectedCompanies = useMemo(() => Array.from(selectionMap.values()), [selectionMap]);

    const toggleCompany = useCallback((company: Company) => {
        if (!company.Id) return;
        setSelectionMap((prev) => {
            const next = new Map(prev);
            if (next.has(company.Id!)) {
                next.delete(company.Id!);
            } else {
                next.set(company.Id!, company);
            }
            return next;
        });
    }, []);

    const toggleAllCompanies = useCallback((companies: Company[]) => {
        setSelectionMap((prev) => {
            const allSelected = companies.every((c) => c.Id && prev.has(c.Id));
            const next = new Map(prev);
            if (allSelected) {
                companies.forEach((c) => c.Id && next.delete(c.Id));
            } else {
                companies.forEach((c) => c.Id && next.set(c.Id, c));
            }
            return next;
        });
    }, []);

    const removeFromSelection = useCallback((id: string) => {
        setSelectionMap((prev) => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => setSelectionMap(new Map()), []);

    const handleBulkStart = useCallback((action: string) => {
        setActiveBulkAction(action);
    }, []);

    const columns = useMemo(
        () => [
            createSelectionColumn(selectedIds, toggleCompany, toggleAllCompanies, results),
            ...createColumns(columnKeys),
        ],
        [selectedIds, toggleCompany, toggleAllCompanies, results]
    );

    useEffect(() => {
        if (!isLoading && inView) {
            window.scrollBy({ top: -1 });
            send({ type: LOAD_MORE });
        }
    }, [inView, isLoading]);

    return (
        <Grid
            gap="2"
            sx={{
                position: 'relative',
                height: 'calc(100vh - 60px)',
                gridTemplateRows: '0.1fr 1fr',
                maxWidth: '100%',
                overflowX: 'auto',
            }}
        >
            <Box
                mb="3"
                sx={{
                    left: (theme) =>
                        !collapsed
                            ? `calc(${get(theme, 'sizes.navExpanded')} + 9px)`
                            : `calc(${get(theme, 'sizes.navCollapsed')} + 9px)`,
                    right: 0,
                    top: '46px',
                    zIndex: 10,
                    borderRadius: 'sm',
                }}
            >
                <Filters
                    data={companiesData}
                    onSearch={(search) => {
                        send({ type: FILTER_COMPANIES, payload: search });
                        send({ type: LOAD_MORE });
                    }}
                />
                {activeBulkAction === 'add-feature-flag' && (
                    <BulkAddFeatureFlagModal
                        companyIds={Array.from(selectionMap.keys())}
                        onClose={() => setActiveBulkAction(null)}
                    />
                )}
                {selectionMap.size > 0 && (
                    <BulkActionBar
                        selectedCompanies={selectedCompanies}
                        onClear={clearSelection}
                        onRemove={removeFromSelection}
                        onStart={handleBulkStart}
                    />
                )}
                <Box>
                    <Text sx={{ color: 'gray.4', fontSize: '12px' }}>{total} results found</Text>
                </Box>
            </Box>
            <Box
                sx={{
                    borderRadius: 'sm',
                    overflowY: 'scroll',
                    zIndex: 2,
                    justifySelf: 'left',
                    width: '100%',
                    backgroundColor: 'background',
                    padding: 2,
                    position: 'relative',
                }}
            >
                {!isError ? (
                    <Table<TableRow>
                        border="0"
                        header={true}
                        sorting={true}
                        columns={columns}
                        // @ts-ignore
                        data={companiesData}
                    />
                ) : (
                    <Box>
                        <Alert variant="danger" mb="2">
                            <Text as="span" mr="1">
                                Your results couldn't be shown because of the following error:
                            </Text>
                            <Text as="span" variant="alert.danger">
                                {getErrorMessage(current?.event?.data)}
                            </Text>
                        </Alert>
                        <Button
                            onClick={() => {
                                send({ type: FIX_ERROR_BY_RETRYING });
                            }}
                        >
                            Try again
                        </Button>
                    </Box>
                )}
                {isLoading ? (
                    <SkeletonLoader
                        columnCount={columnKeys.length + 1}
                        count={
                            results.length === 0 || results.length + TAKE_ITEMS < total
                                ? TAKE_ITEMS
                                : total - results.length
                        }
                    />
                ) : null}
                <Box
                    key="infinite-loader-bottom"
                    ref={ref}
                    sx={{
                        marginTop: '-100vh',
                        height: '100vh',

                        pointerEvents: 'none',
                    }}
                />
            </Box>
        </Grid>
    );
};

export default Companies;
